<template>
  <b-card class="ptr-pts-calculator">
    <template #header>
      <div class="header">
        <span>PTR & PTS Calculator</span>
        <button
          class="reset-btn"
          @click="reset"
        >
          <feather-icon
            size="14"
            icon="RotateCwIcon"
            color="white"
          />
        </button>
      </div>
    </template>

    <b-card-body>
      <!-- Direction Toggle -->
      <div class="direction-toggle">
        <button
          :class="{ active: calculationDirection === 'mrp' }"
          @click="calculationDirection = 'mrp'"
        >
          Calculate from MRP
        </button>
        <button
          :class="{ active: calculationDirection === 'pts' }"
          @click="calculationDirection = 'pts'"
        >
          Calculate from PTS
        </button>
      </div>

      <!-- Mode Tabs -->
      <div class="tabs">
        <button
          :class="{ active: calculationMode === 0 }"
          @click="calculationMode = 0"
        >
          PTR & PTS
        </button>
        <button
          :class="{ active: calculationMode === 1 }"
          @click="calculationMode = 1"
        >
          Margins
        </button>
      </div>

      <!-- Input Section -->
      <div class="input-section">
        <!-- Mode 0: Calculate PTR & PTS from Margins -->
        <div v-if="calculationMode === 0">
          <div class="row-inputs">
            <input
              v-model.number="mrp"
              type="number"
              step="0.01"
              placeholder="MRP"
              @keyup.enter="handleCalculate"
            >
            <input
              v-model.number="gst"
              type="number"
              step="0.01"
              placeholder="GST %"
              @keyup.enter="handleCalculate"
            >
          </div>

          <div class="row-inputs">
            <input
              v-model.number="retailerMargin"
              type="number"
              step="0.01"
              placeholder="Retailer Margin %"
              @keyup.enter="handleCalculate"
            >
            <input
              v-model.number="stockistMargin"
              type="number"
              step="0.01"
              :placeholder="calculationDirection === 'pts' ? 'Stockist Margin %' : 'Stockist Margin % (Optional)'"
              @keyup.enter="handleCalculate"
            >
          </div>
        </div>

        <!-- Mode 1: Calculate Margins from PTR & PTS -->
        <div v-if="calculationMode === 1">
          <div class="row-inputs">
            <input
              v-model.number="mrp"
              type="number"
              step="0.01"
              placeholder="MRP"
              @keyup.enter="handleCalculate"
            >
            <input
              v-model.number="gst"
              type="number"
              step="0.01"
              placeholder="GST %"
              @keyup.enter="handleCalculate"
            >
          </div>

          <div class="row-inputs">
            <input
              v-model.number="ptr"
              type="number"
              step="0.01"
              placeholder="PTR"
              @keyup.enter="handleCalculate"
            >
            <input
              v-model.number="pts"
              type="number"
              step="0.01"
              placeholder="PTS (Optional)"
              @keyup.enter="handleCalculate"
            >
          </div>
        </div>

        <button
          class="calc-btn"
          @click="handleCalculate"
        >
          <feather-icon
            size="14"
            icon="CalculatorIcon"
          />
          Calculate
        </button>
      </div>

      <!-- Results -->
      <div
        v-if="results.calculated"
        class="results"
      >
        <!-- Results for Mode 0: Show PTR & PTS -->
        <div
          v-if="calculationMode === 0"
          class="result-grid"
        >
          <div class="result-item">
            <div class="label">
              PTR
            </div>
            <div class="value">
              ₹{{ results.ptr }}
            </div>
          </div>
          <div
            v-if="results.pts !== null"
            class="result-item"
          >
            <div class="label">
              PTS
            </div>
            <div class="value">
              ₹{{ results.pts }}
            </div>
          </div>
        </div>

        <!-- Results for Mode 1: Show Margins -->
        <div
          v-if="calculationMode === 1"
          class="result-grid"
        >
          <div class="result-item">
            <div class="label">
              Retailer Margin
            </div>
            <div class="value">
              {{ results.retailerMargin }}%
            </div>
          </div>
          <div
            v-if="results.stockistMargin !== null"
            class="result-item"
          >
            <div class="label">
              Stockist Margin
            </div>
            <div class="value">
              {{ results.stockistMargin }}%
            </div>
          </div>
        </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import { BCard, BCardBody } from 'bootstrap-vue'

export default {
  name: 'PTRAndPTSCalculator',
  components: { BCard, BCardBody },
  data() {
    return {
      calculationDirection: 'mrp',
      calculationMode: 0,
      mrp: null,
      gst: null,
      retailerMargin: null,
      stockistMargin: null,
      ptr: null,
      pts: null,
      results: {
        calculated: false,
        ptr: '0.00',
        pts: null,
        retailerMargin: '0.00',
        stockistMargin: null,
        basePrice: '0.00',
      },
    }
  },
  methods: {
    handleCalculate() {
      if (this.calculationMode === 0) {
        if (this.calculationDirection === 'mrp') {
          this.calculatePtrPts()
        } else {
          this.calculatePtsFromMrp()
        }
      } else if (this.calculationDirection === 'mrp') {
        this.calculateMargins()
      } else {
        this.calculateMarginsFromPts()
      }
    },

    calculatePtrPts() {
      if (this.mrp && this.gst !== null && this.retailerMargin !== null) {
        const basePrice = this.mrp / (1 + this.gst / 100)
        const retailerMarginAmount = basePrice * (this.retailerMargin / 100)
        const ptr = basePrice - retailerMarginAmount

        let pts = null
        if (this.stockistMargin !== null && this.stockistMargin !== '') {
          const stockistMarginAmount = ptr * (this.stockistMargin / 100)
          pts = (ptr - stockistMarginAmount).toFixed(2)
        }

        this.results = {
          calculated: true,
          ptr: ptr.toFixed(2),
          pts,
          basePrice: basePrice.toFixed(2),
          retailerMargin: this.retailerMargin.toFixed(2),
          stockistMargin: this.stockistMargin !== null && this.stockistMargin !== ''
            ? this.stockistMargin.toFixed(2)
            : null,
        }
      }
    },

    calculatePtsFromMrp() {
      if (this.mrp && this.gst !== null && this.retailerMargin !== null && this.stockistMargin !== null) {
        const basePrice = this.mrp / (1 + this.gst / 100)
        const ptr = basePrice / (1 + this.retailerMargin / 100)
        const pts = ptr / (1 + this.stockistMargin / 100)

        this.results = {
          calculated: true,
          ptr: ptr.toFixed(2),
          pts: pts.toFixed(2),
          basePrice: basePrice.toFixed(2),
          retailerMargin: this.retailerMargin.toFixed(2),
          stockistMargin: this.stockistMargin.toFixed(2),
        }
      }
    },

    calculateMargins() {
      if (this.mrp && this.gst !== null && this.ptr) {
        const basePrice = this.mrp / (1 + this.gst / 100)
        const retailerMarginPercent = ((basePrice - this.ptr) / basePrice) * 100

        let stockistMarginPercent = null
        if (this.pts !== null && this.pts !== '') {
          stockistMarginPercent = ((this.ptr - this.pts) / this.ptr) * 100
        }

        this.results = {
          calculated: true,
          ptr: this.ptr.toFixed(2),
          pts: this.pts !== null && this.pts !== '' ? this.pts.toFixed(2) : null,
          retailerMargin: retailerMarginPercent.toFixed(1),
          stockistMargin: stockistMarginPercent !== null ? stockistMarginPercent.toFixed(1) : null,
          basePrice: basePrice.toFixed(2),
        }
      }
    },

    calculateMarginsFromPts() {
      if (this.mrp && this.gst !== null && this.ptr) {
        const basePrice = this.mrp / (1 + this.gst / 100)
        const retailerMarginPercent = ((basePrice / this.ptr) - 1) * 100

        let stockistMarginPercent = null
        if (this.pts !== null && this.pts !== '') {
          stockistMarginPercent = ((this.ptr / this.pts) - 1) * 100
        }

        this.results = {
          calculated: true,
          ptr: this.ptr.toFixed(2),
          pts: this.pts !== null && this.pts !== '' ? this.pts.toFixed(2) : null,
          retailerMargin: retailerMarginPercent.toFixed(1),
          stockistMargin: stockistMarginPercent !== null ? stockistMarginPercent.toFixed(1) : null,
          basePrice: basePrice.toFixed(2),
        }
      }
    },

    reset() {
      this.calculationDirection = 'mrp'
      this.mrp = null
      this.gst = null
      this.retailerMargin = null
      this.stockistMargin = null
      this.ptr = null
      this.pts = null
      this.results = {
        calculated: false,
        ptr: '0.00',
        pts: null,
        retailerMargin: '0.00',
        stockistMargin: null,
        basePrice: '0.00',
      }
    },
  },
}
</script>

<style scoped>
.ptr-pts-calculator {
  max-width: 400px;
  margin: 0 auto;
}

/* Header */
.ptr-pts-calculator >>> .card-header {
   background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
 color: white;
  padding: 4px 12px;
  width: 100%;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.header span {
  font-size: 0.85rem;
  font-weight: 600;
}

.reset-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.2s;
}

.reset-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Body */
.ptr-pts-calculator >>> .card-body {
  padding: 0;
}

/* Direction Toggle */
.direction-toggle {
  display: flex;
  gap: 0;
  background: #e3f2fd;
  padding: 4px;
  border-radius: 4px;
}

.direction-toggle button {
  flex: 1;
  padding: 8px;
  background: white;
  border: 1px solid #ddd;
  font-size: 0.75rem;
  font-weight: 500;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
}

.direction-toggle button:first-child {
  border-radius: 4px 0 0 4px;
  border-right: none;
}

.direction-toggle button:last-child {
  border-radius: 0 4px 4px 0;
  border-left: none;
}

.direction-toggle button.active {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

/* Tabs */
.tabs {
  display: flex;
  gap: 0;
  background: #f5f5f5;
  padding: 4px;
  border-radius: 4px;
}

.tabs button {
  flex: 1;
  padding: 10px;
  background: white;
  border: 1px solid #ddd;
  font-size: 0.8rem;
  font-weight: 500;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
}

.tabs button:first-child {
  border-radius: 4px 0 0 4px;
  border-right: none;
}

.tabs button:last-child {
  border-radius: 0 4px 4px 0;
  border-left: none;
}

.tabs button.active {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

/* Input Section */
.input-section {
  padding: 12px;
}

.input-section input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.85rem;
  margin-bottom: 8px;
}

.row-inputs {
  display: flex;
  gap: 8px;
}

.row-inputs input {
  flex: 1;
}

.calc-btn {
  width: 100%;
  padding: 8px;
  margin-top: 0;
  background: linear-gradient(135deg, #28a745 0%, #218838 100%);
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

/* Results */
.results {
  background: #f8f9fa;
  padding: 12px;
  border-top: 1px solid #ddd;
}

.result-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px;
}

.result-item {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px;
  text-align: center;
}

.result-item .label {
  font-size: 0.7rem;
  color: #666;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.result-item .value {
  font-size: 1.2rem;
  color: #333;
  font-weight: 700;
}
</style>
